// Multi-tab coordination for operator alerts (brief §7.5): only the operator's MOST
// RECENTLY FOCUSED tab opens a dialog; the others count. Every tab has its own socket in
// the same airport rooms, so each one receives each alert — nothing is forwarded here, a
// tab only learns who leads (`focus` / `who` / `bye`) and what was acknowledged elsewhere
// (`read`), so a dialog closed in one tab clears the counters in the others.
//
// Degrades to "this tab leads" where BroadcastChannel is missing or throws (an old
// browser, a locked-down profile): one tab still gets every dialog, never none.

type Msg = { k: 'focus' | 'bye'; tab: string } | { k: 'who' } | { k: 'read'; upTo: string };

export interface IAlertsChannel {
  leader(): boolean;
  read(upTo: string): void;
  close(): void;
}

export function openAlertsChannel(
  name: string,
  onRead: (upTo: string) => void,
  win: Window = window,
): IAlertsChannel {
  const me = Math.random().toString(36).slice(2);
  let last: string | null = null;
  let ch: BroadcastChannel | null = null;
  try {
    const Ctor = (win as Window & { BroadcastChannel?: typeof BroadcastChannel }).BroadcastChannel;
    ch = Ctor ? new Ctor(name) : null;
  } catch {
    ch = null;
  }
  const post = (m: Msg): void => {
    try {
      ch?.postMessage(m);
    } catch {
      // a closed channel — this tab simply stops coordinating
    }
  };
  const focus = (): void => {
    last = me;
    post({ k: 'focus', tab: me });
  };
  const bye = (): void => {
    if (last === me) post({ k: 'bye', tab: me });
  };
  if (ch) {
    ch.onmessage = (e: MessageEvent<Msg>) => {
      const m = e.data;
      if (m?.k === 'focus') last = m.tab;
      else if (m?.k === 'who' && last === me) post({ k: 'focus', tab: me });
      else if (m?.k === 'bye' && m.tab === last) last = null;
      else if (m?.k === 'read' && typeof m.upTo === 'string') onRead(m.upTo);
    };
  }
  win.addEventListener('focus', focus);
  win.addEventListener('pagehide', bye);
  // A tab opened in the background asks who leads instead of assuming it does.
  if (win.document.hasFocus()) focus();
  else post({ k: 'who' });
  return {
    leader: () => !ch || last === me || last === null || win.document.hasFocus(),
    read: (upTo) => post({ k: 'read', upTo }),
    close: () => {
      bye();
      win.removeEventListener('focus', focus);
      win.removeEventListener('pagehide', bye);
      ch?.close();
    },
  };
}
